import { css } from '@emotion/react'
import { useEffect, useCallback, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { inject, observer, Provider } from 'mobx-react'
import { getSnapshot } from 'mobx-state-tree'
import { Form, Field } from 'react-final-form'
import { FORM_ERROR } from 'final-form'
import useMediaQuery from '@mui/material/useMediaQuery'
import dayjs from 'dayjs'
import _ from 'lodash'

import { API } from '../../../api'
import {
  Button,
  ButtonsBox,
  PageTitle,
  Spacer,
  Loading,
  MultiButtonInput,
  Label,
  HelpIcon,
  TooltipText,
  MultiLineButton,
} from '../../../components'
import useKeyboardInset from '../../../components/useKeyboardInset'
import { MOBILE, MOBILE_VIEWPORT } from '../../../guided-toolbox/utils/breakpoints'
import { TAX_TYPES } from '../../../constants'
import { reduceValidationError, withRouterProps } from '../../../utils'
import { trackInvestmentsByTaxType } from '../utils'
import SecuritySelector from './components/SecuritySelector'
import SecuritySearchScreen from './components/SecuritySearchScreen'
import SelectedSecurity from './components/SelectedSecurity'
import { AddInvestmentContainer } from '../styled'
import AddNewInvestmentState from './AddNewInvestmentState'
import {
  FocusedFlow,
  FocusedHeading,
  FocusedScroll,
  HeadingRow,
  useScrolledUnder,
} from './AddNewInvestmentStyle'
import PreTaxForm, { preTaxSchema } from './forms/PreTaxForm'
import RothForm, { rothSchema } from './forms/RothForm'
import AfterTaxForm, { afterTaxSchema } from './forms/AfterTaxForm'
import {
  balancesForValueChoice,
  canSaveInvestment,
  initializeValues,
  typeToInvestmentsAPI,
} from './investmentUtils'

/* The lock-up leaves the whole flow, not just the screen it is on. */
const CANCEL_LABEL = 'Cancel adding an investment'

const saveActions = css`
  display: flex;
  gap: 16px;

  ${MOBILE} {
    flex-direction: column-reverse;
    /* The buttons box centers its items so Cancel does not stretch to the taller save buttons,
       which leaves this wrapper at its content width unless it claims the row for itself. */
    width: 100%;
  }
`

const AddNewInvestment = props => {
  const { store, account, location } = props // provides account, investments, and control from AccountStore

  const [state] = useState(() =>
    AddNewInvestmentState.create(
      { status: 'loading', securitySearchStatus: 'done' },
      { store, account }
    )
  )

  const isMobile = useMediaQuery(MOBILE_VIEWPORT)
  const { pinToViewport, viewportHeight, viewportOffsetTop } = useKeyboardInset()
  const { scrolled, handleScroll } = useScrolledUnder()
  // Focused from the tap that asks for the search back, so the keyboard comes with it.
  const searchInputRef = useRef(null)

  const fetchInitialState = useCallback(
    async ({ type, id }) => {
      try {
        state.setStatus('loading')
        const { getAccount, getInvestments } = props.account

        if (type === 'institutional') {
          const { getInstitutionalAccount } = props.store
          await getInstitutionalAccount()
        } else {
          await getAccount({ type, id })
          await getInvestments({ type, id })
        }

        state.setStatus('done')
      } catch (err) {
        console.error(err)
        state.setStatus('error')
      }
    },
    [props, state]
  )

  /** Swaps the chosen investment for the full security record behind the id that was picked. */
  const handleSecuritySelect = useCallback(
    async id => {
      try {
        state.setSecurity(null)
        const security = await API.get(`security/${id}`)
        state.setSecurity(security.data)
      } catch (err) {
        console.error(err)
      }
    },
    [state]
  )

  // An investment handed over by whoever sent the user here arrives as a partial record
  useEffect(() => {
    if (!_.isEmpty(location?.state?.security)) {
      handleSecuritySelect(location.state.security.id)
    }
    // Only on arrival: re-running would throw away an investment the user has since picked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (props.account.account === null) {
      const { type, id } = props.match.params
      fetchInitialState({ type, id })
    } else {
      state.setStatus('done')
    }
  }, [fetchInitialState, props.account.account, props.match.params, state])

  const handleCancel = () => {
    props.navigate(-1)
  }

  const handleClearSecurity = () => {
    state.setSecurity(null)
  }

  /*
   * Hands the search back on mobile. flushSync puts the search field on screen while the tap is
   * still live, because mobile browsers only raise the keyboard for a focus() that happens inside
   * the gesture that asked for it.
   */
  const handleChangeInvestment = () => {
    flushSync(handleClearSecurity)
    searchInputRef.current?.focus()
  }

  const handleSave = async ({ addBalance, preTax, roth, afterTax, resetForm }) => {
    try {
      const { type, id } = props.match.params
      const apiURL = typeToInvestmentsAPI({ type, id })

      // if addBalance is false, it means add the investment of the Not Invested type
      if (addBalance === false) {
        const { id: securityId, securityType, securityName: tickerName } = state.security
        const {
          account: { participantId },
        } = props.account

        await API.post(apiURL, {
          personId: participantId,
          securities: { id: securityId },
          securityType,
          tickerName,
          taxType: TAX_TYPES.notInvested,
        })
      }

      // if addBalance is true, determine preTax, roth, or afterTax forms to submit
      if (addBalance === true && preTax) {
        const {
          account: { participantId },
        } = props.account

        const security = getSnapshot(state.security)
        const { securityType, securityName: tickerName } = security
        const { purchaseDate, quantity, totalCostBasis, totalValue, valuationMethod } = preTax

        await API.post(apiURL, {
          dateOfUserInfo: dayjs().format('YYYY-MM-DD'),
          personId: participantId,
          securities: getSnapshot(state.security),
          securityType,
          taxType: TAX_TYPES.preTax,
          tickerName,
          purchaseDate,
          quantity,
          totalCostBasis: totalCostBasis || 0,
          totalValue,
          valuationMethod,
        })
      }

      if (addBalance === true && roth) {
        const {
          account: { participantId },
        } = props.account

        const security = getSnapshot(state.security)
        const { securityType, securityName: tickerName } = security
        const { purchaseDate, quantity, totalCostBasis, totalValue, valuationMethod } = roth

        await API.post(apiURL, {
          dateOfUserInfo: dayjs().format('YYYY-MM-DD'),
          personId: participantId,
          securities: getSnapshot(state.security),
          securityType,
          taxType: TAX_TYPES.roth,
          tickerName,
          purchaseDate,
          quantity,
          totalCostBasis: totalCostBasis || 0,
          totalValue,
          valuationMethod,
        })
      }

      if (addBalance === true && afterTax) {
        const {
          account: { participantId },
        } = props.account

        const security = getSnapshot(state.security)
        const { securityType, securityName: tickerName } = security
        const { purchaseDate, quantity, totalCostBasis, totalValue, valuationMethod } = afterTax

        await API.post(apiURL, {
          dateOfUserInfo: dayjs().format('YYYY-MM-DD'),
          personId: participantId,
          securities: getSnapshot(state.security),
          securityType,
          taxType: TAX_TYPES.afterTax,
          tickerName,
          purchaseDate,
          quantity,
          totalCostBasis: totalCostBasis || 0,
          totalValue,
          valuationMethod,
        })
      }

      // The following case should only run when investments already added and available for edit
      // if (addBalance === true && !preTax && !roth && !afterTax) {
      //   console.log('At least one Balance Form is required')
      // }

      await props.store.getAccounts()

      if (resetForm) {
        const { type, id } = props.match.params
        state.setSecurity(null)
        await fetchInitialState({ type, id })
      } else {
        handleCancel()
      }
    } catch (err) {
      console.error(err)
      return { [FORM_ERROR]: 'Oops! Something went wrong, please try again later' }
    }
  }

  const validate = async values => {
    const preTax = values.preTax
      ? await preTaxSchema
          .validate(values.preTax, { abortEarly: false })
          .then(() => {})
          .catch(err => reduceValidationError(err))
      : undefined

    const roth = values.roth
      ? await rothSchema
          .validate(values.roth, { abortEarly: false })
          .then(() => {})
          .catch(err => reduceValidationError(err))
      : undefined

    const afterTax = values.afterTax
      ? await afterTaxSchema
          .validate(values.afterTax, { abortEarly: false })
          .then(() => {})
          .catch(err => reduceValidationError(err))
      : undefined

    return _.pickBy({ preTax, roth, afterTax }, _.identity)
  }

  const initialValues = () => {
    const { accountType } = props.match.params
    const {
      showAddBalanceButton,
      showPreTaxForm,
      showRothForm,
      showAfterTaxForm,
      notInvestedAlreadyExistsForSecurity,
    } = state

    return initializeValues({
      accountType,
      showAddBalanceButton,
      showPreTaxForm,
      showRothForm,
      showAfterTaxForm,
      notInvestedAlreadyExistsForSecurity,
    })
  }

  /*
   * Loading and failure are drawn inside the mobile frame too, so arriving on a phone never flashes
   * the app's nav for as long as it takes to fetch the account.
   */
  if (state.status === 'loading' || state.status === 'error') {
    const notice =
      state.status === 'loading' ? (
        <Loading />
      ) : (
        <div
          css={css`
            color: crimson;
          `}>
          Something went wrong, please try again later.
        </div>
      )

    return isMobile ? (
      <FocusedFlow onCancel={handleCancel} cancelLabel={CANCEL_LABEL}>
        <FocusedScroll>{notice}</FocusedScroll>
      </FocusedFlow>
    ) : (
      notice
    )
  }

  const {
    account: { type },
  } = props.account
  const { accountType } = props.match.params
  const { security, showPreTaxForm, showRothForm, showAfterTaxForm, singleFormOnly } = state

  // Investments already added as "not invested" are offered up front, before anything is typed
  const notInvestedInvestments = trackInvestmentsByTaxType(accountType)
    ? props.account.notInvestedInvestments
    : []

  /* The balances for the chosen investment, and the actions that save them. */
  const balanceForm = security && (
    <Form
      onSubmit={handleSave}
      validate={validate}
      initialValues={initialValues()}
      /* A fresh object every render, so the default shallow compare reads every re-render of this
         page as new initial values and throws away what has been filled in — including the choice
         between # of shares and a balance, which the focusin listener behind the keyboard inset
         then undoes on the very click that opens the shares field. */
      initialValuesEqual={_.isEqual}
      subscription={{ submitting: true, submitError: true, invalid: true, values: true }}
      render={({ form, handleSubmit, submitting, values }) => (
        <div>
          {/* Ask Add Balance or Not Invested here */}
          {values.showAddBalanceButton && (
            <div>
              <Label isDrawer fontSize='18px'>
                Value <HelpIcon tooltip={TooltipText.investmentValue()} />
              </Label>
              <Field
                name='addBalance'
                render={({ input }) => (
                  <MultiButtonInput
                    buttons={[
                      { text: 'Add Balance', value: true },
                      { text: 'Not Invested', value: false },
                    ]}
                    mobileToggle
                    margin='0'
                    name='addBalance'
                    onChange={(name, value) => {
                      form.reset({
                        ...values,
                        ...balancesForValueChoice(value, initialValues()),
                      })
                      input.onChange(value)
                    }}
                    value={input.value}
                  />
                )}
              />
            </div>
          )}

          {showPreTaxForm && values.addBalance && (
            <PreTaxForm
              security={security}
              type={type}
              hideNotInvested
              onlyTaxType={singleFormOnly}
            />
          )}

          {showRothForm && values.addBalance && (
            <RothForm
              security={security}
              type={type}
              hideNotInvested
              onlyTaxType={singleFormOnly}
            />
          )}

          {showAfterTaxForm && values.addBalance && (
            <AfterTaxForm
              security={security}
              type={type}
              hideNotInvested
              onlyTaxType={singleFormOnly}
            />
          )}

          {values.addBalance === true && !showPreTaxForm && !showRothForm && !showAfterTaxForm && (
            <div
              css={css`
                color: #e31e27;
                font-size: 1rem;
              `}>
              Investment already added and available for edit
            </div>
          )}

          <ButtonsBox
            css={css`
              /* Keep Cancel centered instead of stretching it to match the taller save buttons. */
              align-items: center;
            `}>
            {/* Mobile cancels from the lock-up pinned to the top of the screen instead. */}
            {!isMobile && (
              <Button variant='secondary' onClick={handleCancel} disabled={submitting}>
                Cancel
              </Button>
            )}
            <div css={saveActions}>
              <MultiLineButton
                variant='tertiary'
                label='Save'
                sublabel='return to summary'
                onClick={() => {
                  form.change('resetForm', false)
                  handleSubmit()
                }}
                disabled={submitting || !canSaveInvestment(values)}
              />
              <MultiLineButton
                label='Save +'
                sublabel='add another investment'
                onClick={() => {
                  form.change('resetForm', true)
                  handleSubmit()
                }}
                disabled={submitting || !canSaveInvestment(values)}
              />
            </div>
          </ButtonsBox>
        </div>
      )}
    />
  )

  /*
   * A phone gives the flow the whole screen. `dvh` does not account for the on-screen keyboard, so
   * while it is up the shell takes the measured visual-viewport height instead — that is what keeps
   * the footer above the keyboard rather than behind it. See useKeyboardInset.
   */
  if (isMobile) {
    const shellStyle = pinToViewport
      ? { top: `${viewportOffsetTop}px`, height: `min(100dvh, ${viewportHeight}px)` }
      : undefined

    return (
      <Provider newInvestment={state}>
        <FocusedFlow
          onCancel={handleCancel}
          cancelLabel={CANCEL_LABEL}
          scrolled={scrolled}
          style={shellStyle}>
          {security ? (
            <FocusedScroll onScroll={handleScroll}>
              <HeadingRow>
                <FocusedHeading>Let&rsquo;s add an investment</FocusedHeading>
              </HeadingRow>
              <SelectedSecurity security={security} onChange={handleChangeInvestment} />
              <Spacer space='20px' />
              {balanceForm}
            </FocusedScroll>
          ) : (
            <SecuritySearchScreen
              accountType={accountType}
              notInvestedInvestments={notInvestedInvestments}
              onSelect={handleSecuritySelect}
              onScroll={handleScroll}
              inputRef={searchInputRef}
            />
          )}
        </FocusedFlow>
      </Provider>
    )
  }

  return (
    <Provider newInvestment={state}>
      <div
        css={css`
          max-width: 1200px;
          margin: auto;
          padding: 1rem;
        `}>
        <AddInvestmentContainer>
          <PageTitle>Let's add an investment</PageTitle>

          {/* Investment Type and Security Selection */}
          <SecuritySelector
            accountType={accountType}
            security={security}
            notInvestedInvestments={notInvestedInvestments}
            onSelect={handleSecuritySelect}
            onClear={handleClearSecurity}
          />

          <Spacer space='20px' />

          {balanceForm}

          {/* Provide a Cancel button when a security hasn't been selected yet */}
          {!security && (
            <ButtonsBox>
              <Button onClick={handleCancel} variant='secondary'>
                Cancel
              </Button>
            </ButtonsBox>
          )}
        </AddInvestmentContainer>
      </div>
    </Provider>
  )
}

export default withRouterProps(inject('store', 'account')(observer(AddNewInvestment)))
